import React, { useContext, useEffect, useState } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Form, Badge, Spinner, Alert } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast, Toaster } from "react-hot-toast";
import { FaMicroscope, FaFlask, FaCalendarAlt, FaUserMd, FaPaw, FaCheckCircle, FaSave, FaArrowLeft, FaExclamationTriangle, FaEdit, FaPrint } from "react-icons/fa";
import { MdDateRange, MdAccessTime } from "react-icons/md";

export default function PetLabTestResultPage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const { petLabTestId } = useParams();
    const AuthToken = localStorage.getItem("authToken");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [petLabTest, setPetLabTest] = useState(null);
    const [template, setTemplate] = useState(null);
    const [resultValues, setResultValues] = useState({});
    const [existingResults, setExistingResults] = useState({});
    const [errors, setErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [originalValues, setOriginalValues] = useState({});

    useEffect(() => {
        if (petLabTestId) {
            fetchTemplateAndData();
        }
    }, [petLabTestId]);

    const fetchTemplateAndData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-lab-test/${petLabTestId}/result-form`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                setPetLabTest(response.data.pet_lab_test);
                setTemplate(response.data.template);

                // Set existing results
                const existingVals = response.data.results || {};
                setExistingResults(existingVals);
                setResultValues(existingVals);
                setOriginalValues(existingVals);

                // Check if results exist and set editing mode
                const hasResults = Object.keys(existingVals).length > 0;
                setIsEditing(!hasResults); // If no results, start in edit mode
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error(t('failed_to_load_template'));
        } finally {
            setLoading(false);
        }
    };

    const handleValueChange = (fieldId, value) => {
        setResultValues(prev => ({
            ...prev,
            [fieldId]: value
        }));

        if (errors[fieldId]) {
            setErrors(prev => ({ ...prev, [fieldId]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!template) return true;

        template.sections?.forEach(section => {
            section.fields?.forEach(field => {
                if (field.is_required && (!resultValues[field.id] || resultValues[field.id] === '')) {
                    newErrors[field.id] = t('this_field_is_required');
                }
            });
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const hasChanges = () => {
        return JSON.stringify(resultValues) !== JSON.stringify(originalValues);
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error(t('please_fill_all_required_fields'));
            return;
        }

        if (!hasChanges()) {
            toast.info(t('no_changes_to_save'));
            return;
        }

        setSubmitting(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-lab-test/result`,
                {
                    pet_lab_test_id: parseInt(petLabTestId),
                    values: resultValues
                },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                toast.success(t('results_saved_successfully'));
                setOriginalValues({ ...resultValues });
                setExistingResults({ ...resultValues });
                setIsEditing(false);

                // Refresh data to get updated status
                setTimeout(() => {
                    fetchTemplateAndData();
                }, 1000);
            }
        } catch (error) {
            console.error("Error saving results:", error);
            toast.error(error.response?.data?.message || t('failed_to_save_results'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setResultValues({ ...originalValues });
        setErrors({});
        setIsEditing(false);
    };

    const handlePrint = () => {
        window.print();
    };

    const isValueAbnormal = (field, value) => {
        if (!value || !field.normal_min || !field.normal_max) return false;
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return false;

        if (field.normal_min !== null && numValue < parseFloat(field.normal_min)) return true;
        if (field.normal_max !== null && numValue > parseFloat(field.normal_max)) return true;
        return false;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    const getResultDisplay = (field, value) => {
        if (!value) return <span className="text-muted">—</span>;

        const isAbnormal = isValueAbnormal(field, value);

        if (field.type === 'number') {
            return (
                <span className={isAbnormal ? "fw-bold text-danger" : ""}>
                    {value} {field.unit || ''}
                    {isAbnormal && (
                        <Badge bg="danger" className="ms-2">
                            {t('abnormal')}
                        </Badge>
                    )}
                </span>
            );
        }

        return (
            <span className={isAbnormal ? "fw-bold text-danger" : ""}>
                {value}
            </span>
        );
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 mb-0">{t('loading_lab_test_data')}</p>
                </div>
            </PageLayout>
        );
    }

    if (!petLabTest) {
        return (
            <PageLayout>
                <Alert variant="danger" className="text-center py-5">
                    <FaExclamationTriangle size={40} className="mb-2" />
                    <h5>{t('lab_test_not_found')}</h5>
                    <ButtonComponent
                        className="btn btn-primary mt-3"
                        onClick={() => navigate(-1)}
                        text={t('go_back')}
                    />
                </Alert>
            </PageLayout>
        );
    }

    const dateTime = formatDateTime(petLabTest.date);
    const hasExistingResults = Object.keys(existingResults).length > 0;
    const isCompleted = petLabTest.status === 'completed';

    return (
        <PageLayout>
            <Toaster position="top-right" />

            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <h4 className="mb-0 d-flex align-items-center gap-2">
                    <FaMicroscope className="text-primary" />
                    {t('lab_test_results')}
                </h4>
                <div className="d-flex gap-2">
                    {hasExistingResults && !isEditing && (
                        <>
                            <ButtonComponent
                                className="btn btn-info"
                                onClick={() =>
                                    window.open(
                                        `${process.env.REACT_APP_URL}/lab-report/${petLabTestId}`,
                                        '_blank'
                                    )
                                }
                                text={
                                    <>
                                        <FaPrint className="me-2" />
                                        {t('print')}
                                    </>
                                }
                            />
                            <ButtonComponent
                                className="btn btn-primary"
                                onClick={handleEdit}
                                text={
                                    <>
                                        <FaEdit className="me-2" />
                                        {t('edit_results')}
                                    </>
                                }
                            />
                        </>
                    )}
                    <ButtonComponent
                        className="btn btn-secondary"
                        onClick={() => navigate(-1)}
                        text={
                            <>
                                <FaArrowLeft className="me-2" />
                                {t('back')}
                            </>
                        }
                    />
                </div>
            </div>

            <Card className="p-4">
                {/* Pet & Test Information - Read Only Section */}
                <div className="mb-4">
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-muted mb-1">{t('pet name')}</Form.Label>
                                <div className="fw-bold">
                                    <FaPaw className="me-2 text-primary" />
                                    {petLabTest.appointment?.pet?.name || 'N/A'}
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-muted mb-1">{t('breed')}</Form.Label>
                                <div className="fw-bold">
                                    {petLabTest.appointment?.pet?.breed?.name || 'N/A'}
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-muted mb-1">{t('owner')}</Form.Label>
                                <div className="fw-bold">
                                    {petLabTest.appointment?.customer?.name || 'N/A'}
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-muted mb-1">{t('test name')}</Form.Label>
                                <div className="fw-bold">
                                    <FaFlask className="me-2 text-success" />
                                    {petLabTest.lab_test?.name || 'N/A'}
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-muted mb-1">{t('test date')}</Form.Label>
                                <div className="fw-bold">
                                    <MdDateRange className="me-2" />
                                    {dateTime?.date || 'N/A'}
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-muted mb-1">{t('test time')}</Form.Label>
                                <div className="fw-bold">
                                    <MdAccessTime className="me-2" />
                                    {dateTime?.time || 'N/A'}
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-muted mb-1">{t('doctor')}</Form.Label>
                                <div className="fw-bold">
                                    <FaUserMd className="me-2" />
                                    {petLabTest.appointment?.doctor?.name || 'Unknown'}
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-muted mb-1">{t('status')}</Form.Label>
                                <div>
                                    <Badge bg={isCompleted ? 'success' : 'warning'}>
                                        {isCompleted ? t('completed') : t('processing')}
                                    </Badge>
                                    {hasExistingResults && (
                                        <Badge bg="info" className="ms-2">
                                            <FaCheckCircle className="me-1" size={10} />
                                            {t('results_added')}
                                        </Badge>
                                    )}
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    {petLabTest.note && petLabTest.note.length > 0 && (
                        <Row className="mt-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="text-muted mb-1">{t('doctor_note')}</Form.Label>
                                    <div className="text-secondary bg-light p-2 rounded">
                                        {petLabTest.note[0].note}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                </div>

                <hr className="my-4" />

                {/* Results Section */}
                {template && template.sections && template.sections.length > 0 ? (
                    <>
                        <div className="mb-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                {hasExistingResults && !isEditing ? t('test_results') : t('enter_results')}
                            </h5>
                            {!isEditing && hasExistingResults && (
                                <Badge bg="success" pill className="px-3 py-2">
                                    {t('results_finalized')}
                                </Badge>
                            )}
                        </div>

                        <div className="table-responsive">
                            <table className="table table-bordered align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '30%' }}>{t('Test')}</th>
                                        <th style={{ width: '15%' }}>{t('Unit')}</th>
                                        <th style={{ width: '25%' }}>{t('Normal Range')}</th>
                                        <th style={{ width: '30%' }}>{t('Result')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {template.sections.map((section, sectionIndex) => (
                                        <React.Fragment key={section.id || sectionIndex}>
                                            {/* SECTION TITLE */}
                                            {section.title && (
                                                <tr className="table-secondary">
                                                    <td colSpan="4" className="fw-bold">
                                                        {section.title}
                                                    </td>
                                                </tr>
                                            )}

                                            {/* FIELDS */}
                                            {section.fields && section.fields.length > 0 ? (
                                                section.fields.map((field) => {
                                                    const value = resultValues[field.id] || '';
                                                    const originalValue = originalValues[field.id] || '';
                                                    const isAbnormal = isValueAbnormal(field, value);
                                                    const error = errors[field.id];
                                                    const hasChanged = value !== originalValue;

                                                    return (
                                                        <tr key={field.id} className={hasChanged && isEditing ? "table-warning" : ""}>
                                                            {/* TEST NAME */}
                                                            <td>
                                                                {field.field_name}
                                                                {field.is_required && (
                                                                    <span className="text-danger ms-1">*</span>
                                                                )}
                                                                {isEditing && hasChanged && (
                                                                    <Badge bg="warning" className="ms-2" text="dark">
                                                                        {t('modified')}
                                                                    </Badge>
                                                                )}
                                                            </td>

                                                            {/* UNIT */}
                                                            <td>{field.unit || '—'}</td>

                                                            {/* NORMAL RANGE */}
                                                            <td>
                                                                {field.normal_min || field.normal_max
                                                                    ? `${field.normal_min || '—'} - ${field.normal_max || '—'} ${field.unit || ''}`
                                                                    : '—'}
                                                                {field.normal_min && field.normal_max && (
                                                                    <small className="text-muted d-block">
                                                                        {t('reference_range')}
                                                                    </small>
                                                                )}
                                                            </td>

                                                            {/* RESULT INPUT OR DISPLAY */}
                                                            <td>
                                                                {isEditing ? (
                                                                    <>
                                                                        <Form.Control
                                                                            type={field.type === 'number' ? 'number' : 'text'}
                                                                            step={field.type === 'number' ? "any" : undefined}
                                                                            value={value}
                                                                            onChange={(e) =>
                                                                                handleValueChange(field.id, e.target.value)
                                                                            }
                                                                            placeholder={t('enter_value')}
                                                                            isInvalid={!!error}
                                                                            className={isAbnormal ? "border-danger" : ""}
                                                                        />
                                                                        {error && (
                                                                            <div className="text-danger small mt-1">
                                                                                {error}
                                                                            </div>
                                                                        )}
                                                                        {!error && isAbnormal && value && (
                                                                            <div className="text-danger small mt-1">
                                                                                <FaExclamationTriangle className="me-1" />
                                                                                {t('value_outside_normal_range')}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    getResultDisplay(field, value)
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="4">
                                                        <Alert variant="info" className="mb-0">
                                                            {t('no_fields_in_section')}
                                                        </Alert>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Action Buttons */}
                        {isEditing ? (
                            <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                                <ButtonComponent
                                    className="btn btn-secondary"
                                    onClick={handleCancel}
                                    text={t('cancel')}
                                />
                                <ButtonComponent
                                    className="btn btn-success"
                                    onClick={handleSubmit}
                                    disabled={submitting || !hasChanges()}
                                    text={
                                        submitting ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                {t('saving')}
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="me-2" />
                                                {hasExistingResults ? t('update_results') : t('save_results')}
                                            </>
                                        )
                                    }
                                />
                            </div>
                        ) : hasExistingResults ? (
                            <div className="alert alert-success text-center py-3 mt-4">
                                <FaCheckCircle size={24} className="me-2" />
                                <strong>{t('results_completed')}</strong> - {t('results_have_been_recorded')}
                            </div>
                        ) : null}
                    </>
                ) : (
                    <Alert variant="warning" className="text-center py-4">
                        <FaFlask size={40} className="mb-2 text-warning" />
                        <h5>{t('no_template_found')}</h5>
                        <p className="mb-0">{t('no_template_available_for_this_test')}</p>
                        <ButtonComponent
                            className="btn btn-primary mt-3"
                            onClick={() => navigate(-1)}
                            text={t('go_back')}
                        />
                    </Alert>
                )}
            </Card>

            <style jsx>{`
                @media print {
                    .btn, .Toaster, .mc-breadcrumb, .d-flex.justify-content-between {
                        display: none !important;
                    }
                    .card {
                        box-shadow: none !important;
                        padding: 0 !important;
                    }
                    .table {
                        border: 1px solid #dee2e6;
                    }
                }
            `}</style>
        </PageLayout>
    );
}